import { useState } from "react";
import type { CSSProperties, ReactNode, MouseEvent } from "react";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

// antd Radio.Group + Radio.Button (segmented buttons) -> MUI ToggleButtonGroup +
// ToggleButton. antd Group onChange(e) exposes e.target.value; MUI onChange(e, value).
// We adapt to the antd signature so call sites are unchanged.

export interface RadioGroupProps {
  value?: string | number | boolean | null;
  defaultValue?: string | number | boolean | null;
  // antd RadioChangeEvent.target.value is `any`; match it so typed setters assign.
  onChange?: (e: { target: { value: any } }) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  disabled?: boolean;
  buttonStyle?: "solid" | "outline";
  size?: "small" | "middle" | "large";
  optionType?: "default" | "button";
  name?: string;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  "data-cy"?: string;
  "data-testid"?: string;
}

const Group = ({
  value, defaultValue, onChange, disabled, size, children, style, className,
  "data-cy": dataCy, "data-testid": dataTestid,
}: RadioGroupProps) => {
  // antd semantics: `value` makes the group controlled; `defaultValue` alone only
  // seeds the selection, which then follows clicks.
  const [innerValue, setInnerValue] = useState(defaultValue);
  return (
    <ToggleButtonGroup
      exclusive
      value={(value !== undefined ? value : innerValue) ?? null}
      disabled={disabled}
      size={size === "large" ? "large" : "small"}
      onChange={(_e, val) => {
        if (val === null) return;
        setInnerValue(val);
        onChange?.({ target: { value: val } });
      }}
      style={style}
      className={className}
      data-cy={dataCy}
      data-testid={dataTestid}
    >
      {children}
    </ToggleButtonGroup>
  );
};

export interface RadioButtonProps {
  value: string | number | boolean;
  children?: ReactNode;
  disabled?: boolean;
  name?: string;
  style?: CSSProperties;
  className?: string;
  onClick?: (e: MouseEvent) => void;
  "data-cy"?: string;
  "data-testid"?: string;
}

const Button = ({ value, children, disabled, style, className, onClick, "data-cy": dataCy, "data-testid": dataTestid }: RadioButtonProps) => (
  // pin typography so every segmented tab matches regardless of ad-hoc label styling
  // or the app's root font-size (antd Radio.Button was 14px, no uppercase).
  <ToggleButton value={value} disabled={disabled} style={style} className={className} onClick={onClick} data-cy={dataCy} data-testid={dataTestid} sx={{ fontSize: 14, fontWeight: 500, textTransform: "none" }}>
    {children}
  </ToggleButton>
);

// Bare <Radio> (rare here) behaves like a Radio.Button inside a group.
export const Radio = ({ value, children, disabled, style, className }: RadioButtonProps) => (
  <ToggleButton value={value} disabled={disabled} style={style} className={className}>
    {children}
  </ToggleButton>
);

Radio.Group = Group;
Radio.Button = Button;

export default Radio;
